import React, { useEffect, useState } from "react";
import axios from "axios";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import Card from "../Components/Card";
import { BRAND } from "../css/brand";
import { RefreshCw } from "lucide-react";

export default function DatewiseCustomersChart() {
  const [data, setData] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState(() => {
    const today = new Date();
    return today.toISOString().slice(0, 7); // YYYY-MM
  });

  // 🔹 Auth Data
  const authUserString = sessionStorage.getItem("authUserData1");
  const authUser = JSON.parse(authUserString);
  const user = authUser?.data?.[0] || {};
  const role = user?.role;
  const userId = user?.id;

  // 🔹 Utility: Get all days in month
  const getDaysInMonth = (year, month) => {
    const days = [];
    const lastDay = new Date(year, month + 1, 0).getDate(); // ✅ get correct number of days
    for (let i = 1; i <= lastDay; i++) {
      const date = new Date(year, month, i).toISOString().split("T")[0];
      days.push(date);
    }
    return days;
  };


  // 🔹 Utility: Get last 7 days
  const getLast7Days = () => {
    const days = [];
    const today = new Date();
    for (let i = 6; i >= 0; i--) {
      const d = new Date(today);
      d.setDate(today.getDate() - i);
      days.push(d.toISOString().split("T")[0]);
    }
    return days;
  };

  // 🔹 Tooltip
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const firstKey = payload[0]?.dataKey;
      const isCustomerChart = firstKey === "recommended";
      const total = payload.reduce((acc, cur) => acc + (cur.value || 0), 0);

      return (
        <div className="bg-white shadow-md rounded-lg border border-gray-200 p-2 text-sm">
          <p className="font-semibold text-gray-800">{label}</p>
          {isCustomerChart ? (
            <>
              <p className="text-blue-600">
                Recommended: {payload[0]?.value || 0}
              </p>
              <p className="text-indigo-600">
                Chalte Chalte: {payload[1]?.value || 0}
              </p>
              <p className="text-gray-700 font-medium border-t mt-1 pt-1">
                Total: {total}
              </p>
            </>
          ) : (
            <p className="text-blue-600 font-medium">
              Assigned Policies: {total}
            </p>
          )}
        </div>
      );
    }
    return null;
  };

  // 🔹 Build API URL
  const buildApiUrl = (from, to) => {
    if (role === 2 || role === 5) {
      const base = "https://spillas.com/api/v1/get-customers-chart";
      const params = new URLSearchParams({
        from_date: from,
        to_date: to,
      });
      if (role === 2) params.append("company_id", user?.company_id || 26);
      if (role === 5) params.append("dist_id", userId);
      return `${base}?${params.toString()}`;
    }

    const base = "https://spillas.com/api/v1/keys/report";
    const params = new URLSearchParams({
      sender_id: userId || 26,
      mode: 1,
      company_id: user?.company_id || 26,
      from_date: from,
      to_date: to,
      per_page: 10000,
    });
    return `${base}?${params.toString()}`;
  };

  // 🔹 Fetch API Data
  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);

      let fromDate, toDate, allDates;

      if (role === 2 || role === 5) {
        const days = getLast7Days();
        fromDate = days[0];
        toDate = days[days.length - 1];
        allDates = days;
      } else {
       const [year, month] = selectedMonth.split("-").map(Number);

        // ✅ Always start from 1st of the selected month
        const firstDay = new Date(year, month - 1, 1);

        // ✅ Get last day of the *same* month
        const lastDay = new Date(year, month, 0);

        // ✅ Ensure only current month days are included (not previous)
        fromDate = firstDay.toISOString().split("T")[0];
        toDate = lastDay.toISOString().split("T")[0];

        // ✅ Generate correct days within selected month
        allDates = [];
        for (let d = 1; d <= lastDay.getDate(); d++) {
          const day = new Date(year, month - 1, d).toISOString().split("T")[0];
          allDates.push(day);
}

      }

      const url = buildApiUrl(fromDate, toDate);
      const res = await axios.get(url);
      const response = res.data;

      if (role === 2 || role === 5) {
        if (response?.chart_data) {
          const apiData = response.chart_data.map((d) => ({
            date: d.date,
            recommended: Number(d.total_type_1) || 0,
            chalte_chalte: Number(d.total_type_3) || 0,
          }));

          const mergedData = allDates.map((date) => {
            const found = apiData.find((item) => item.date === date);
            return found || { date, recommended: 0, chalte_chalte: 0 };
          });

          setData(mergedData);
          setSummary(response.summary || null);
        } else {
          setData(allDates.map((date) => ({ date, recommended: 0, chalte_chalte: 0 })));
          setSummary(null);
        }
      } else if (role === 3 || role === 4) {
        if (response?.data) {
          const apiData = response.data.map((d) => ({
            date: d.created_at.split("T")[0],
            qty: Number(d.qty) || 0,
          }));

          const grouped = apiData.reduce((acc, curr) => {
            acc[curr.date] = (acc[curr.date] || 0) + curr.qty;
            return acc;
          }, {});

          const mergedData = allDates.map((date) => ({
            date,
            qty: grouped[date] || 0,
          }));

          setData(mergedData);
          const totalQty = Object.values(grouped).reduce((sum, val) => sum + val, 0);
          setSummary({ total_qty: totalQty });
        } else {
          setData(allDates.map((date) => ({ date, qty: 0 })));
          setSummary(null);
        }
      }
    } catch (err) {
      console.error("Error fetching chart data:", err);
      setError("Failed to load data.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [selectedMonth]);

  return (
    <Card
      brand={BRAND}
      title={
        role === 3 || role === 4
          ? "Assigned Policy Chart (Month-wise)"
          : "Customer Chart (Last 7 Days)"
      }
    >
      {/* 🔹 Top Controls */}
      <div className="flex flex-col sm:flex-row justify-between items-center gap-3 mt-2">
        {role === 3 || role === 4 ? (
          <div className="flex items-center gap-2 text-sm">
            <label htmlFor="month" className="text-gray-700">
              Select Month:
            </label>
            <input
              id="month"
              type="month"
              value={selectedMonth}
              onChange={(e) => setSelectedMonth(e.target.value)}
              className="border rounded-md px-2 py-1 text-sm focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
        ) : (
          <div />
        )}

        <button
          onClick={fetchData}
          className="inline-flex items-center gap-1 text-xs bg-blue-600 text-white px-3 py-1 rounded hover:bg-blue-700"
        >
          <RefreshCw size={14} />
          Refresh
        </button>
      </div>

      {/* 🔹 Chart Section */}
      {loading ? (
        <p className="text-center text-gray-500 py-6">Loading data...</p>
      ) : error ? (
        <p className="text-center text-red-500 py-6">{error}</p>
      ) : data.length === 0 ? (
        <p className="text-center text-gray-500 py-6">No data found.</p>
      ) : (
        <>
          {role === 3 || role === 4 ? (
            // 🟧 Scrollable Chart for Policy Chart
            <div className="h-64 mt-4 overflow-x-auto scrollbar-thin scrollbar-thumb-gray-300">
              <div style={{ width: `${data.length * 40}px`, minWidth: "600px" }}>
                <ResponsiveContainer width="100%" height={250}>
                  <BarChart data={data}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis
                      dataKey="date"
                      interval={0}
                      angle={0}
                      textAnchor="end"
                      height={60}
                      tickFormatter={(date) => date.slice(8)} // ✅ only show day of month
                    />
                    <YAxis allowDecimals={false} />
                    <Tooltip content={<CustomTooltip />} />
                    <Legend />
                    <Bar
                      dataKey="qty"
                      name="Assigned Policies"
                      fill={BRAND.blue}
                      barSize={10}
                      radius={[6, 6, 0, 0]}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          ) : (
            // 🟦 Normal Chart for Customer Chart
            <div className="h-64 mt-4">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={data}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis
                    dataKey="date"
                    interval={0}
                    angle={0}
                    textAnchor="end"
                    
                    height={60}
                    tickFormatter={(date) => date.slice(5)}
                  />
                  <YAxis allowDecimals={false} />
                  <Tooltip content={<CustomTooltip />} />
                  <Legend />
                  <Bar
                    dataKey="recommended"
                    name="Recommended"
                    fill={BRAND.blue}
                    radius={[6, 6, 0, 0]}
                  />
                  <Bar
                    dataKey="chalte_chalte"
                    name="Chalte Chalte"
                    fill={BRAND.darkBlue}
                    radius={[6, 6, 0, 0]}
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}

          {/* 🔹 Summary */}
          {summary && (
            <div className="grid grid-cols-3 text-center mt-3 text-sm text-gray-700">
              {role === 2 || role === 5 ? (
                <>
                  <div>
                    <p className="font-semibold text-blue-600">
                      {summary.total_recommended}
                    </p>
                    <p>Recommended</p>
                  </div>
                  <div>
                    <p className="font-semibold text-indigo-600">
                      {summary.totale_chalte_chalte}
                    </p>
                    <p>Chalte Chalte</p>
                  </div>
                  <div>
                    <p className="font-semibold text-slate-600">
                      {summary.total}
                    </p>
                    <p>Total Customers</p>
                  </div>
                </>
              ) : (
                <div className="col-span-3">
                  <p className="font-semibold text-blue-600">
                    {summary.total_qty}
                  </p>
                  <p>Total Assigned Policies</p>
                </div>
              )}
            </div>
          )}
        </>
      )}
    </Card>
  );
}


// import React, { useEffect, useState } from "react";
// import axios from "axios";
// import {
//   BarChart,
//   Bar,
//   XAxis,
//   YAxis,
//   CartesianGrid,
//   Tooltip,
//   Legend,
//   ResponsiveContainer,
// } from "recharts";
// import Card from "../Components/Card";
// import { BRAND } from "../css/brand";
// import { RefreshCw } from "lucide-react";

// export default function DatewiseCustomersChart() {
//   const [data, setData] = useState([]);
//   const [loading, setLoading] = useState(false);
//   const [error, setError] = useState(null);
//   const [summary, setSummary] = useState(null);

//   // 🔹 Get auth data from session
//   const authUserString = sessionStorage.getItem("authUserData1");
//   const authUser = JSON.parse(authUserString);
//   const user = authUser?.data?.[0] || {};
//   const role = user?.role;
//   const userId = user?.id;

//   // 🔹 Date range (last 7 days)
//   const today = new Date();
//   const toDate = today.toISOString().split("T")[0];
//   const fromDate = new Date(today);
//   fromDate.setDate(fromDate.getDate() - 6);
//   const from = fromDate.toISOString().split("T")[0];

//   // 🔹 Generate all last 7 dates
//   const getLast7Dates = () => {
//     const dates = [];
//     const baseDate = new Date(fromDate);
//     for (let i = 0; i < 7; i++) {
//       const d = new Date(baseDate);
//       d.setDate(fromDate.getDate() + i);
//       dates.push(d.toISOString().split("T")[0]);
//     }
//     return dates;
//   };

//   // 🔹 Custom Tooltip for chart
//   const CustomTooltip = ({ active, payload, label }) => {
//     if (active && payload && payload.length) {
//       const recommended =
//         payload.find((p) => p.dataKey === "recommended")?.value || 0;
//       const chalteChalte =
//         payload.find((p) => p.dataKey === "chalte_chalte")?.value || 0;
//       const total = recommended + chalteChalte;
//       return (
//         <div className="bg-white shadow-md rounded-lg border border-gray-200 p-2 text-sm">
//           <p className="font-semibold text-gray-800">{label}</p>
//           <p className="text-blue-600">Recommended: {recommended}</p>
//           <p className="text-indigo-600">Chalte Chalte: {chalteChalte}</p>
//           <p className="text-gray-700 font-medium border-t mt-1 pt-1">
//             Total: {total}
//           </p>
//         </div>
//       );
//     }
//     return null;
//   };

//   // 🔹 Build API URL based on role
//   const buildApiUrl = () => {
//     const base = "https://spillas.com/api/v1/get-customers-chart";
//     const params = new URLSearchParams({
//       from_date: from,
//       to_date: toDate,
//     });

//     if (role === 2) params.append("company_id", user?.company_id || 26);
//     else if (role === 3) params.append("cnf_id", userId);
//     else if (role === 4) params.append("ss_id", userId);
//     else if (role === 5) params.append("dist_id", userId);
//     else if (role === 6) params.append("dist_id", userId);

//     return `${base}?${params.toString()}`;
//   };

//   // 🔹 Fetch API data
//   const fetchData = async () => {
//     try {
//       setLoading(true);
//       setError(null);

//       const url = buildApiUrl();
//       const res = await axios.get(url);
//       const response = res.data;

//       const allDates = getLast7Dates();

//       if (response?.chart_data) {
//         const apiData = response.chart_data.map((d) => ({
//           date: d.date,
//           recommended: Number(d.total_type_1) || 0,
//           chalte_chalte: Number(d.total_type_3) || 0,
//         }));

//         // ✅ Ensure all 7 dates appear
//         const mergedData = allDates.map((date) => {
//           const found = apiData.find((item) => item.date === date);
//           return found || { date, recommended: 0, chalte_chalte: 0 };
//         });

//         setData(mergedData);
//         setSummary(response.summary || null);
//       } else {
//         // Even if no chart_data, show 7 zero days
//         const emptyData = allDates.map((date) => ({
//           date,
//           recommended: 0,
//           chalte_chalte: 0,
//         }));
//         setData(emptyData);
//         setSummary(null);
//       }
//     } catch (err) {
//       console.error("Error fetching customers chart:", err);
//       setError("Failed to load data.");
//     } finally {
//       setLoading(false);
//     }
//   };

//   useEffect(() => {
//     fetchData();
//   }, []); // Run once on mount

//   return (
//     <Card brand={BRAND} title="Customers (Last 7 Days)">
//       {/* Refresh Button */}
//       <div className="flex justify-end mt-2">
//         <button
//           onClick={fetchData}
//           className="inline-flex items-center gap-1 text-xs bg-blue-600 text-white px-3 py-1 rounded hover:bg-blue-700"
//         >
//           <RefreshCw size={14} />
//           Refresh
//         </button>
//       </div>

//       {/* Loading / Error / Chart */}
//       {loading ? (
//         <p className="text-center text-gray-500 py-6">Loading data...</p>
//       ) : error ? (
//         <p className="text-center text-red-500 py-6">{error}</p>
//       ) : data.length === 0 ? (
//         <p className="text-center text-gray-500 py-6">No data found.</p>
//       ) : (
//         <>
//           <div className="h-56">
//             <ResponsiveContainer width="100%" height="100%">
//               <BarChart data={data}>
//                 <CartesianGrid strokeDasharray="3 3" />
//                 {/* ✅ Ensure all 7 dates are shown */}
//                 <XAxis
//                   dataKey="date"
//                   interval={0}
//                   tickFormatter={(date) => date.slice(5)} // show MM-DD only
//                 />
//                 <YAxis allowDecimals={false} />
//                 <Tooltip content={<CustomTooltip />} />
//                 <Legend />
//                 <Bar
//                   dataKey="recommended"
//                   name="Recommended"
//                   fill={BRAND.blue}
//                   radius={[6, 6, 0, 0]}
//                 />
//                 <Bar
//                   dataKey="chalte_chalte"
//                   name="Chalte Chalte"
//                   fill={BRAND.darkBlue}
//                   radius={[6, 6, 0, 0]}
//                 />
//               </BarChart>
//             </ResponsiveContainer>
//           </div>

//           {summary && (
//             <div className="grid grid-cols-3 text-center mt-3 text-sm text-gray-700">
//               <div>
//                 <p className="font-semibold text-blue-600">
//                   {summary.total_recommended}
//                 </p>
//                 <p>Recommended</p>
//               </div>
//               <div>
//                 <p className="font-semibold text-indigo-600">
//                   {summary.totale_chalte_chalte}
//                 </p>
//                 <p>Chalte Chalte</p>
//               </div>
//               <div>
//                 <p className="font-semibold text-slate-600">{summary.total}</p>
//                 <p>Total Customers</p>
//               </div>
//             </div>
//           )}
//         </>
//       )}
//     </Card>
//   );
// }


// import React, { useEffect, useState } from "react";
// import axios from "axios";
// import {
//   BarChart,
//   Bar,
//   XAxis,
//   YAxis,
//   CartesianGrid,
//   Tooltip,
//   Legend,
//   ResponsiveContainer,
// } from "recharts";
// import Card from "../Components/Card";
// import { BRAND } from "../css/brand";
// import { RefreshCw } from "lucide-react";

// export default function DatewiseCustomersChart() {
//   const [data, setData] = useState([]);
//   const [loading, setLoading] = useState(false);
//   const [error, setError] = useState(null);
//   const [summary, setSummary] = useState(null);

//   // 🔹 Get auth data from session
//   const authUserString = sessionStorage.getItem("authUserData1");
//   const authUser = JSON.parse(authUserString);
//   const user = authUser?.data?.[0] || {};
//   const role = user?.role;
//   const userId = user?.id;

//   // 🔹 Date range (last 7 days)
//   const today = new Date();
//   const toDate = today.toISOString().split("T")[0];
//   const fromDate = new Date(today);
//   fromDate.setDate(fromDate.getDate() - 6);
//   const from = fromDate.toISOString().split("T")[0];

//   // 🔹 Generate all last 7 dates
//   const getLast7Dates = () => {
//     const dates = [];
//     const d = new Date(fromDate);
//     for (let i = 0; i < 7; i++) {
//       const newDate = new Date(d);
//       newDate.setDate(fromDate.getDate() + i);
//       dates.push(newDate.toISOString().split("T")[0]);
//     }
//     return dates;
//   };

//   // 🔹 Custom Tooltip for chart
//   const CustomTooltip = ({ active, payload, label }) => {
//     if (active && payload && payload.length) {
//       const recommended = payload.find((p) => p.dataKey === "recommended")?.value || 0;
//       const chalteChalte = payload.find((p) => p.dataKey === "chalte_chalte")?.value || 0;
//       const total = recommended + chalteChalte;
//       return (
//         <div className="bg-white shadow-md rounded-lg border border-gray-200 p-2 text-sm">
//           <p className="font-semibold text-gray-800">{label}</p>
//           <p className="text-blue-600">Recommended: {recommended}</p>
//           <p className="text-indigo-600">Chalte Chalte: {chalteChalte}</p>
//           <p className="text-gray-700 font-medium border-t mt-1 pt-1">
//             Total: {total}
//           </p>
//         </div>
//       );
//     }
//     return null;
//   };

//   // 🔹 Build API URL based on role
//   const buildApiUrl = () => {
//     const base = "https://spillas.com/api/v1/get-customers-chart";
//     const params = new URLSearchParams({
//       from_date: from,
//       to_date: toDate,
//     });

//     if (role === 2) params.append("company_id", user?.company_id || 26);
//     else if (role === 3) params.append("cnf_id", userId);
//     else if (role === 4) params.append("ss_id", userId);
//     else if (role === 5) params.append("dist_id", userId);
//     else if (role === 6) params.append("dist_id", userId);

//     return `${base}?${params.toString()}`;
//   };

//   // 🔹 Fetch API data
//   const fetchData = async () => {
//     try {
//       setLoading(true);
//       setError(null);

//       const url = buildApiUrl();
//       const res = await axios.get(url);
//       const response = res.data;

//       if (response?.chart_data) {
//         const apiData = response.chart_data.map((d) => ({
//           date: d.date,
//           recommended: Number(d.total_type_1) || 0,
//           chalte_chalte: Number(d.total_type_3) || 0,
//         }));

//         const allDates = getLast7Dates();
//         const mergedData = allDates.map((date) => {
//           const found = apiData.find((item) => item.date === date);
//           return found || { date, recommended: 0, chalte_chalte: 0 };
//         });

//         setData(mergedData);
//         setSummary(response.summary || null);
//       } else {
//         setData([]);
//         setSummary(null);
//       }
//     } catch (err) {
//       console.error("Error fetching customers chart:", err);
//       setError("Failed to load data.");
//     } finally {
//       setLoading(false);
//     }
//   };

//   useEffect(() => {
//     fetchData();
//   }, []); // Run once on mount

//   return (
//     <Card brand={BRAND} title="Customers (Last 7 Days)">
//       {/* Refresh Button */}
//       <div className="flex justify-end mt-2">
//         <button
//           onClick={fetchData}
//           className="inline-flex items-center gap-1 text-xs bg-blue-600 text-white px-3 py-1 rounded hover:bg-blue-700"
//         >
//           <RefreshCw size={14} />
//           Refresh
//         </button>
//       </div>

//       {/* Loading / Error / Chart */}
//       {loading ? (
//         <p className="text-center text-gray-500 py-6">Loading data...</p>
//       ) : error ? (
//         <p className="text-center text-red-500 py-6">{error}</p>
//       ) : data.length === 0 ? (
//         <p className="text-center text-gray-500 py-6">No data found.</p>
//       ) : (
//         <>
//           <div className="h-56">
//             <ResponsiveContainer width="100%" height="100%">
//               <BarChart data={data}>
//                 <CartesianGrid strokeDasharray="3 3" />
//                 <XAxis dataKey="date" />
//                 <YAxis />
//                 <Tooltip content={<CustomTooltip />} />
//                 <Legend />
//                 <Bar
//                   dataKey="recommended"
//                   name="Recommended"
//                   fill={BRAND.blue}
//                   radius={[6, 6, 0, 0]}
//                 />
//                 <Bar
//                   dataKey="chalte_chalte"
//                   name="Chalte Chalte"
//                   fill={BRAND.darkBlue}
//                   radius={[6, 6, 0, 0]}
//                 />
//               </BarChart>
//             </ResponsiveContainer>
//           </div>

//           {summary && (
//             <div className="grid grid-cols-3 text-center mt-3 text-sm text-gray-700">
//               <div>
//                 <p className="font-semibold text-blue-600">
//                   {summary.total_recommended}
//                 </p>
//                 <p>Recommended</p>
//               </div>
//               <div>
//                 <p className="font-semibold text-indigo-600">
//                   {summary.totale_chalte_chalte}
//                 </p>
//                 <p>Chalte Chalte</p>
//               </div>
//               <div>
//                 <p className="font-semibold text-slate-600">{summary.total}</p>
//                 <p>Total Customers</p>
//               </div>
//             </div>
//           )}
//         </>
//       )}
//     </Card>
//   );
// }


// import React, { useEffect, useState } from "react";
// import axios from "axios";
// import {
//   BarChart,
//   Bar,
//   XAxis,
//   YAxis,
//   CartesianGrid,
//   Tooltip,
//   Legend,
//   ResponsiveContainer,
// } from "recharts";
// import Card from "../Components/Card";
// import { BRAND } from "../css/brand";
// import { RefreshCw } from "lucide-react";

// export default function DatewiseCustomersChart() {
//   const [data, setData] = useState([]);
//   const [loading, setLoading] = useState(false);
//   const [error, setError] = useState(null);
//   const [summary, setSummary] = useState(null);

//   // 🔹 Get auth data
//   const authUserString = sessionStorage.getItem("authUserData1");
//   const authUser = JSON.parse(authUserString);
//   const company_id = authUser?.data?.[0]?.company_id || 26;

//   // 🔹 Calculate last 7 days range
//   const today = new Date();
//   const toDate = today.toISOString().split("T")[0];
//   const fromDate = new Date(today);
//   fromDate.setDate(fromDate.getDate() - 6);
//   const from = fromDate.toISOString().split("T")[0];

//   // 🔹 Generate all last 7 dates
//   const getLast7Dates = () => {
//     const dates = [];
//     const d = new Date(fromDate);
//     for (let i = 0; i < 7; i++) {
//       const newDate = new Date(d);
//       newDate.setDate(fromDate.getDate() + i);
//       dates.push(newDate.toISOString().split("T")[0]);
//     }
//     return dates;
//   };

//   // 🔹 Custom Tooltip
//   const CustomTooltip = ({ active, payload, label }) => {
//     if (active && payload && payload.length) {
//       const recommended = payload.find((p) => p.dataKey === "recommended")?.value || 0;
//       const chalteChalte = payload.find((p) => p.dataKey === "chalte_chalte")?.value || 0;
//       const total = recommended + chalteChalte;
//       return (
//         <div className="bg-white shadow-md rounded-lg border border-gray-200 p-2 text-sm">
//           <p className="font-semibold text-gray-800">{label}</p>
//           <p className="text-blue-600">Recommended: {recommended}</p>
//           <p className="text-indigo-600">Chalte Chalte: {chalteChalte}</p>
//           <p className="text-gray-700 font-medium border-t mt-1 pt-1">
//             Total: {total}
//           </p>
//         </div>
//       );
//     }
//     return null;
//   };

//   // 🔹 Fetch API
//   const fetchData = async () => {
//     if (!company_id) return;

//     const url = `https://spillas.com/api/v1/get-customers-chart?from_date=${from}&to_date=${toDate}&company_id=${company_id}`;
//     try {
//       setLoading(true);
//       setError(null);

//       const res = await axios.get(url);
//       const response = res.data;

//       if (response?.chart_data) {
//         // Step 1: Format API data
//         const apiData = response.chart_data.map((d) => ({
//           date: d.date,
//           recommended: Number(d.total_type_1) || 0,
//           chalte_chalte: Number(d.total_type_3) || 0,
//         }));

//         // Step 2: Fill missing days
//         const allDates = getLast7Dates();
//         const mergedData = allDates.map((date) => {
//           const found = apiData.find((item) => item.date === date);
//           return (
//             found || {
//               date,
//               recommended: 0,
//               chalte_chalte: 0,
//             }
//           );
//         });

//         setData(mergedData);
//         setSummary(response.summary || null);
//       } else {
//         setData([]);
//         setSummary(null);
//       }
//     } catch (err) {
//       console.error("Error fetching customers chart:", err);
//       setError("Failed to load data.");
//     } finally {
//       setLoading(false);
//     }
//   };

//   useEffect(() => {
//     fetchData();
//   }, []);

//   return (
//     <Card brand={BRAND} title="Customers (Last 7 Days)">

//               <div className="flex justify-end mt-2">
//                     <button
//                     onClick={fetchData}
//                     className="inline-flex items-center gap-1 text-xs bg-blue-600 text-white px-3 py-1 rounded hover:bg-blue-700"
//                     >
//                     <RefreshCw size={14} />
//                     Refresh
//                     </button>
//                 </div>
//       {loading ? (
//         <p className="text-center text-gray-500 py-6">Loading data...</p>
//       ) : error ? (
//         <p className="text-center text-red-500 py-6">{error}</p>
//       ) : data.length === 0 ? (
//         <p className="text-center text-gray-500 py-6">No data found.</p>
//       ) : (
//         <>
//           {/* 🔹 Chart */}
//           <div className="h-56">
//             <ResponsiveContainer width="100%" height="100%">
//               <BarChart data={data}>
//                 <CartesianGrid strokeDasharray="3 3" />
//                 <XAxis dataKey="date" />
//                 <YAxis />
//                 <Tooltip content={<CustomTooltip />} />
//                 <Legend />
//                 <Bar
//                   dataKey="recommended"
//                   name="Recommended"
//                   fill={BRAND.blue}
//                   radius={[6, 6, 0, 0]}
//                 />
//                 <Bar
//                   dataKey="chalte_chalte"
//                   name="Chalte Chalte"
//                   fill={BRAND.darkBlue}
//                   radius={[6, 6, 0, 0]}
//                 />
//               </BarChart>
//             </ResponsiveContainer>
//           </div>

//           {/* 🔹 Summary */}
//           {summary && (
//             <div className="grid grid-cols-3 text-center mt-3 text-sm text-gray-700">
//               <div>
//                 <p className="font-semibold text-blue-600">
//                   {summary.total_recommended}
//                 </p>
//                 <p>Recommended</p>
//               </div>
//               <div>
//                 <p className="font-semibold text-indigo-600">
//                   {summary.totale_chalte_chalte}
//                 </p>
//                 <p>Chalte Chalte</p>
//               </div>
//               <div>
//                 <p className="font-semibold text-slate-600">{summary.total}</p>
//                 <p>Total Customers</p>
//               </div>
//             </div>
//           )}
//         </>
//       )}


//     </Card>
//   );
// }